import { createApi } from '@reduxjs/toolkit/dist/query/react';

import serverRoutes from '@app/utils/server-routes';

import {
  GPTAllIntegrationListObjectType,
  GPTIntegrationListObjectType,
  GPTIntegrationMutationObject,
  GPTIntegrationObjectType,
  GptIntegrationsBillingType,
  GPTModelsSupportObjectType,
  IntegrationAccessKeyCreateObject,
  IntegrationAccessKeyObject,
  SpecialIntegrationKeysObjectType,
} from '@app/interfaces/settings.type';

import { baseQuery } from '../baseQuery';
export const integrationSettingsApi = createApi({
  reducerPath: 'integrationSettingsApi',
  tagTypes: ['integration', 'gptIntegration', 'gptIntegrationSingle', 'specialIntegrationKeys'],
  baseQuery: baseQuery,
  endpoints: (build) => ({
    getIntegrationsAccessKeysList: build.query<IntegrationAccessKeyObject[], void>({
      query: () => ({
        url: serverRoutes.integrationsAccessKeys,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ access_key }) => ({
                type: 'integration' as const,
                access_key,
              })),
              'integration',
            ]
          : ['integration'],
    }),
    getIntegrationsAccessKey: build.query<IntegrationAccessKeyObject, { access_key: string }>({
      query: (params) => ({
        url: serverRoutes.integrationsAccessKey,
        params,
      }),
    }),
    createIntegrationsAccessKey: build.mutation<IntegrationAccessKeyCreateObject, { name: string }>(
      {
        query: (body) => ({
          url: serverRoutes.integrationsAccessKey,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['integration'],
      },
    ),
    editIntegrationsAccessKey: build.mutation<
      IntegrationAccessKeyObject,
      { body: { name: string }; params: { access_key: string } }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.integrationsAccessKey,
        method: 'PUT',
        body,
        params,
      }),
      invalidatesTags: ['integration'],
    }),
    deleteIntegrationsAccessKey: build.mutation({
      query: (params: { access_key: string }) => ({
        url: serverRoutes.integrationsAccessKey,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: ['integration'],
    }),
    // GPT INTEGRATION API
    getGptIntegrationsListAll: build.query<GPTAllIntegrationListObjectType, void>({
      query: () => ({
        url: serverRoutes.gpt_integration_all,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.gpt_integrations.map(({ gpt_integration_id }) => ({
                type: 'gptIntegration' as const,
                gpt_integration_id,
              })),
              'gptIntegration',
            ]
          : ['gptIntegration'],
    }),
    getGptIntegrationsListSupport: build.query<GPTModelsSupportObjectType, void>({
      query: () => ({
        url: serverRoutes.gpt_integration_support,
      }),
    }),
    getGptIntegrationsListUsers: build.query<GPTIntegrationListObjectType, void>({
      query: () => ({
        url: serverRoutes.gpt_integration_users,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.gpt_integrations.map(({ gpt_integration_id }) => ({
                type: 'gptIntegration' as const,
                gpt_integration_id,
              })),
              'gptIntegration',
            ]
          : ['gptIntegration'],
    }),
    getUserGptIntegrationById: build.query<
      GPTIntegrationObjectType,
      { gpt_integration_id: string }
    >({
      query: (params) => ({
        url: serverRoutes.gpt_integration,
        params,
      }),
      providesTags: ['gptIntegrationSingle'],
    }),
    createUserGptIntegration: build.mutation<
      GPTIntegrationObjectType,
      GPTIntegrationMutationObject
    >({
      query: (body) => ({
        url: serverRoutes.gpt_integration,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['gptIntegration'],
    }),
    editUserGptIntegration: build.mutation<
      GPTIntegrationObjectType,
      { body?: Partial<GPTIntegrationMutationObject>; params: { gpt_integration_id: string } }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.gpt_integration,
        method: 'PUT',
        body,
        params,
      }),
      invalidatesTags: ['gptIntegration', 'gptIntegrationSingle'],
    }),
    deleteUserGptIntegration: build.mutation<
      GPTIntegrationObjectType,
      { gpt_integration_id: string }
    >({
      query: (params) => ({
        url: serverRoutes.gpt_integration,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: ['gptIntegration'],
    }),
    // system integration is default flag
    editSystemGptIntegrationIsDefault: build.mutation<
      GPTIntegrationObjectType,
      { default_integration_id: number }
    >({
      query: (params) => ({
        url: serverRoutes.gpt_integration_system_default_flag,
        method: 'PUT',
        params,
      }),
      invalidatesTags: ['gptIntegration', 'gptIntegrationSingle'],
    }),
    // system integration is default flag
    // GPT INTEGRATION API
    // gpt integrations billing
    getGptIntegrationsBillingTable: build.query<GptIntegrationsBillingType, void>({
      query: () => ({
        url: serverRoutes.gpt_integration_billing,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.gpt_integrations.user.map(({ gpt_integration: { id } }) => ({
                type: 'gptIntegration' as const,
                id,
              })),
              'gptIntegration',
            ]
          : ['gptIntegration'],
    }),
    // gpt integrations billing
    // SPECIAL INTEGRATION
    getSpecialIntegrationKeys: build.query<SpecialIntegrationKeysObjectType, void>({
      query: () => ({
        url: serverRoutes.special_integration_keys,
      }),
      providesTags: ['specialIntegrationKeys'],
    }),
    mutateSpecialIntegrationKeys: build.mutation<
      SpecialIntegrationKeysObjectType,
      SpecialIntegrationKeysObjectType
    >({
      query: (body) => ({
        url: serverRoutes.special_integration_keys,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['specialIntegrationKeys'],
    }),
    // SPECIAL INTEGRATION
  }),
});

export const {
  useGetIntegrationsAccessKeysListQuery,
  useCreateIntegrationsAccessKeyMutation,
  useEditIntegrationsAccessKeyMutation,
  useDeleteIntegrationsAccessKeyMutation,
  useLazyGetIntegrationsAccessKeyQuery,
  // GPT INTEGRATION
  useGetGptIntegrationsListAllQuery,
  useGetGptIntegrationsListSupportQuery, //
  useGetGptIntegrationsListUsersQuery, //
  useCreateUserGptIntegrationMutation, //
  useEditUserGptIntegrationMutation, //
  useDeleteUserGptIntegrationMutation, //
  useLazyGetUserGptIntegrationByIdQuery, //
  // gpt integrations billing
  useGetGptIntegrationsBillingTableQuery,
  useEditSystemGptIntegrationIsDefaultMutation,
  // SPECIAL INTEGRATION
  useGetSpecialIntegrationKeysQuery,
  useMutateSpecialIntegrationKeysMutation,
} = integrationSettingsApi;
